import React from 'react';
import { Box, Typography, Switch } from '@mui/material';
import SimpleInput from '../Input/SimpleInput';
import FormSelect from './FormSelect';

const DynamicAttributeRenderer = ({ 
  attributes = [], 
  values = {}, 
  onChange, 
  isLoading = false 
}) => {
  const handleAttributeChange = (attributeId, value) => {
    if (onChange) {
      onChange(attributeId, value);
    }
    // console.log('Attribute changed:',attributeId, value); 
  };

  const renderAttribute = (attribute) => {
    // Validate attribute has valid ID
    if (!attribute.id || attribute.id === 'null' || attribute.id === 'undefined') {
      console.error('❌ DynamicAttributeRenderer: Invalid attribute ID:', attribute);
      return null;
    }
    
    // Handle both old format (string value) and new format (object with value and uuid)
    const valueOrObject = values[attribute.id];
    const value = typeof valueOrObject === 'object' ? valueOrObject.value : valueOrObject;
    const formName = `attributes[${String(attribute.id).replace(/-/g, '_')}]`;

    switch (attribute.type) {
      case 'checkbox':
        return (
          <Box key={attribute.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">{attribute.content}</Typography>
            <Switch
              name={formName}
              checked={value || false}
              onChange={(e) => handleAttributeChange(attribute.id, e.target.checked)}
            />
          </Box>
        );

      case 'text':
        return (
          <Box key={attribute.id}>
            <SimpleInput
              type="text"
              name={formName}
              value={value || ''}
              onChange={(e) => handleAttributeChange(attribute.id, e.target.value)}
              placeholder={`Enter ${attribute.content}`}
              label={attribute.content}
            />
          </Box>
        );

      case 'number':
        return (
          <Box key={attribute.id}>
            <SimpleInput
              type="number"
              name={formName}
              value={value || ''}
              onChange={(e) => handleAttributeChange(attribute.id, e.target.value)}
              placeholder={`Enter ${attribute.content}`}
              label={attribute.content}
            />
          </Box>
        );

      case 'select':
        let options = [];
        
        if (Array.isArray(attribute.options)) {
          // Handle array format
          options = attribute.options.map(option => ({
            value: option,
            label: option
          }));
        } else if (attribute.options && typeof attribute.options === 'object') {
          // Handle object format (key-value pairs)
          options = Object.entries(attribute.options).map(([key, value]) => ({
            value: value, // Use the display value as both value and label
            label: value
          }));
         
        }

       

        return (
          <Box key={attribute.id}>
            <FormSelect
              name={formName}
              label={attribute.content}
              value={value || ''}
              onChange={(event) => handleAttributeChange(attribute.id, event.target.value)} // Adjust based on FormSelect
              options={options}
              placeholder={`Select ${attribute.content}`}
              searchable={true}
            />
          </Box>
        );

      default:
        return (
          <Box key={attribute.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">{attribute.content}</Typography>
            <Typography variant="body2" color="textSecondary">
              Unsupported attribute type: {attribute.type}
            </Typography>
          </Box>
        );
    }
  };

  if (isLoading) {
    return (
      <Typography variant="body2" color="textSecondary">
        Loading attributes...
      </Typography>
    );
  }

  if (!attributes || attributes.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        No attributes found for this section.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
      {attributes.map(attribute => renderAttribute(attribute))}
    </Box>
  );
};

export default DynamicAttributeRenderer;